import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { COLORS } from "constants/colors";

const validationSteps = [
  {
    src: "/lovable-uploads/validation_screen1.png",
    text: "Spot missing data, outliers, and errors fast to ensure clean, reliable analysis",
  },
  {
    src: "/lovable-uploads/validation_screen_outliers.png",
    text: "Detect extreme or unusual data points that might distort your results",
  },
  {
    src: "/lovable-uploads/validation_screen_completeness.png",
    text: "Quickly identify missing values and understand how they may affect your analysis",
  },
  {
    src: "/lovable-uploads/validation_screen_validity.png",
    text: "Flag suspicious or illogical entries  ensuring data accuracy and reliability",
  },
];

const ValidationSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPrevIndex(currentIndex);
      setCurrentIndex((prev) => (prev + 1) % validationSteps.length);
    }, 3500); // Change every 3.5 seconds

    return () => clearTimeout(timer);
  }, [currentIndex]);

  const { text } = validationSteps[currentIndex];

  return (
    <Box
      ref={ref}
      component="section"
      sx={{
        backgroundColor: COLORS.cardsBackground,
        py: { xs: 6, md: 12 }, // Reduced padding on mobile
        width: "100vw",
        position: "relative",
        textAlign: "center",
        overflow: "hidden",
        px: { xs: 2, sm: 3, md: 0 }, // Add horizontal padding on mobile
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
      >
        {/* Title */}
        <Typography
          variant={isMobile ? "h4" : "h3"}
          fontWeight="bold"
          mb={2}
          color="#1A1A1A"
          sx={{
            //fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" }, // Responsive font size
            lineHeight: { xs: 1.2, md: 1.3 },
          }}
        >
          Automatic Data Validation
        </Typography>

        {/* Subtitle */}
        <Typography
          variant={isMobile ? "h6" : "h5"}
          color={COLORS.gray900}
          mb={{ xs: 4, md: 6 }} // Reduced margin on mobile
          sx={{
            fontWeight: 400,
            maxWidth: 800,
            mx: "auto",
            lineHeight: 1.5,
            //fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" }, // Responsive font size
            px: { xs: 1, sm: 2 }, // Add horizontal padding on mobile
          }}
        >
          Genie quickly scans your data for errors, identifying missing values, outliers, and unusual entries,
          ensuring your data is clean and reliable
        </Typography>
      </motion.div>

      {/* Image Container */}
      <Box
        sx={{
          position: "relative",
          width: { xs: "90%", sm: "80%", md: "50%" }, // Responsive width
          maxWidth: "100vw",
          mx: "auto",
          height: { xs: 300, sm: 400, md: 600 }, // Responsive height
        }}
      >
        <AnimatePresence initial={false}>
          {prevIndex !== null && prevIndex !== currentIndex && (
            <motion.img
              key={validationSteps[prevIndex].src}
              src={validationSteps[prevIndex].src}
              alt={`Validation ${prevIndex}`}
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: { xs: 8, md: 16 }, // Smaller border radius on mobile
                boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
              }}
            />
          )}

          <motion.img
            key={validationSteps[currentIndex].src}
            src={validationSteps[currentIndex].src}
            alt={`Validation ${currentIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: { xs: 8, md: 16 }, // Smaller border radius on mobile
              boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
            }}
          />
        </AnimatePresence>
      </Box>

      {/* Animated Text Below Image */}
      <motion.div
        key={`desc-${currentIndex}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ marginTop: "2rem" }}
      >
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: { xs: "100%", md: 700 },
            mx: "auto",
            fontSize: { xs: "1rem", sm: "1.125rem", md: "1.15rem" }, // Responsive font size
            fontStyle: "italic",
            lineHeight: 1.6,
            px: { xs: 2, sm: 3, md: 0 }, // Add horizontal padding on mobile
          }}
        >
          {text}
        </Typography>
      </motion.div>
    </Box>
  );
};

export default ValidationSection;
